import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { useTranslation } from "react-i18next";

import "./styles/ChangeBonusCyclist.css";

// On garde uniquement les composants nécessaires
import SelectNewCyclistsBonus from "./changeBonus/SelectNewCyclistsBonus.js";
import SelectRaces from "./changeBonus/SelectRaces";
import SelectBonusRace from "./changeBonus/SelectBonusRace";

const ChangeBonusCyclist = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const apiUrl = process.env.REACT_APP_API_URL;

  // --- STATES ---
  const [step, setStep] = useState(1);
  const [teamId, setTeamId] = useState(1); // Par défaut 1, ou à récupérer via useParams()

  // Données de l'équipe
  const [selectedCyclists, setSelectedCyclists] = useState([]); // Liste des coureurs de l'équipe (pour choisir le bonus)
  const [cyclistsBonus, setCyclistsBonus] = useState([]); // Le coureur bonus choisi
  
  // Données des courses
  const [races, setRaces] = useState([]); // Toutes les courses disponibles
  const [selectedRaces, setSelectedRaces] = useState([]); // Les courses choisies par l'utilisateur
  const [bonusRaces, setBonusRaces] = useState([]); // La course bonus choisie

  // --- NAVIGATION ---
  const nextStep = () => {
    if (step < 3) setStep(step + 1);
  };

  const prevStep = () => {
    if (step > 1) setStep(step - 1);
  };

  // --- SAUVEGARDE ---
  const saveData = () => {
    return new Promise((resolve, reject) => {
      if (!user) {
        navigate("/logIn");
        reject(t("changeTeam.errors.notConnected"));
        return;
      }

      // On s'assure d'inclure les courses obligatoires si elles ne sont pas dans selectedRaces
      // (Logique conservée de ton code original)
      const obligatoryRaces = races.filter(
        (race) => race.groupCompetitionId === 0 || race.groupCompetitionId === 3
      );
      
      // Note: On filtre pour éviter les doublons si obligatoryRaces est déjà dans selectedRaces
      // ou on envoie tout au backend qui gérera le tri. Ici je garde ta logique :
      const finalSelectedRaces = [...obligatoryRaces, ...selectedRaces]; 
      // Petit nettoyage des doublons au cas où :
      const uniqueRaces = Array.from(new Set(finalSelectedRaces.map(a => a.raceId)))
        .map(id => finalSelectedRaces.find(a => a.raceId === id));

      const playerTeamData = {
        // On renvoie les cyclistes tels quels car on ne les modifie pas ici
        selectedNewCyclists: [], // Vide car on ne fait pas d'échange
        selectedPreviousCyclist: selectedCyclists, // On renvoie l'équipe actuelle
        cyclistsBonus,
        selectedRaces: uniqueRaces,
        bonusRaces,
        teamId,
      };

      fetch(`${apiUrl}/saveChangeBonusPlayerTeam`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(playerTeamData),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Erreur HTTP: ${response.status}`);
          return response.json();
        })
        .then((data) => resolve(data))
        .catch((error) => {
          console.error("Error:", error);
          reject(error);
        });
    });
  };

  // --- CHARGEMENT DES DONNÉES (Races & Team Data) ---
  useEffect(() => {
    if (!user) {
      navigate("/logIn");
      return;
    }

    const fetchRaces = async () => {
      try {
        const response = await fetch(`${apiUrl}/races-list`);
        const data = await response.json();

        const formattedData = data
          // 1. On filtre d'abord pour ne garder que teamId = 1
          .filter((race) => race.period !== 4) 
          // 2. Ensuite on formate la date
          .map((race) => ({
            ...race,
            date_start: new Date(race.date_start)
              .toLocaleDateString("en-GB")
              .replace(/\//g, "-"),
          }));

        setRaces(formattedData);
      } catch (error) {
        console.error("Erreur lors de la récupération des courses:", error);
      }
    };

    const fetchTeamData = async () => {
      try {
        const response = await fetch(`${apiUrl}/userTeam/${teamId}`, {
          credentials: "include",
        });

        if (!response.ok) throw new Error("Erreur fetch team");

        const data = await response.json();
        
        // PRÉ-CHARGEMENT DES DONNÉES EXISTANTES
        if (data) {
          // 1. Les cyclistes de l'équipe (pour pouvoir choisir le bonus parmi eux)
          setSelectedCyclists(data.selectedCyclists || []);
          
          // 2. Le bonus cycliste actuel
          setCyclistsBonus(data.cyclistsBonus || []);

          // 3. Les courses sélectionnées actuelles
          setSelectedRaces(data.selectedRaces || []);

          // 4. La course bonus actuelle
          setBonusRaces(data.bonusRaces || []);
        }
      } catch (error) {
        console.error("Erreur chargement données équipe:", error);
      }
    };

    if (teamId) {
      fetchRaces();
      fetchTeamData();
    }
  }, [apiUrl, user, navigate, teamId]);


  // --- RENDU DES ÉTAPES ---
  switch (step) {
    case 1:
      // ÉTAPE 1 : CHOIX DU COUREUR BONUS
      // On réutilise SelectNewCyclistsBonus.
      // Attention : ce composant attendait probablement 'selectedNewCyclists' dans ton ancien code.
      // Ici, on lui passe 'selectedCyclists' (notre équipe actuelle) pour qu'il affiche la liste.
      return (
        <SelectNewCyclistsBonus
          // On passe l'équipe complète dans les deux props pour être sûr qu'il affiche la liste
          selectedCyclists={selectedCyclists} 
          selectedNewCyclists={selectedCyclists} 
          selectedActualCyclists={[]} // Pas pertinent ici
          
          cyclistsBonus={cyclistsBonus}
          setCyclistsBonus={setCyclistsBonus}
          
          nextStep={nextStep}
          prevStep={null} // Pas de retour possible à l'étape 0
          teamId={teamId}
          saveData={null} // On ne sauvegarde pas encore
        />
      );

    case 2:
      // ÉTAPE 2 : CHOIX DES COURSES
      return (
        <SelectRaces
          selectedRaces={selectedRaces}
          setSelectedRaces={setSelectedRaces}
          races={races}
          // On passe aussi les bonusRaces pour affichage si besoin, mais on ne les set pas ici
          setBonusRaces={setBonusRaces}
          bonusRaces={bonusRaces}
          
          nextStep={nextStep}
          prevStep={prevStep}
        />
      );

    case 3:
      // ÉTAPE 3 : CHOIX DE LA COURSE BONUS ET SAUVEGARDE
      return (
        <SelectBonusRace
          selectedRaces={selectedRaces}
          setBonusRaces={setBonusRaces}
          bonusRaces={bonusRaces}
          races={races}
          
          prevStep={prevStep}
          saveData={saveData} // C'est ici qu'on valide tout
        />
      );

    default:
      return <div>{t("changeTeam.errors.unknownStep")}</div>;
  }
};

export default ChangeBonusCyclist;